import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MediaFile, useMediaStore } from '../store/media';

interface MediaUploadProps {
  projectId: string;
  onUpload?: (media: MediaFile) => void;
  maxFiles?: number;
  selectedMedia?: MediaFile[];
  onMediaSelect?: (media: MediaFile[]) => void;
}

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const ALLOWED_AUDIO_TYPES = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/aac', 'audio/ogg', 'audio/webm', 'audio/x-m4a', 'audio/mp4'];
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB
const MAX_AUDIO_SIZE = 50 * 1024 * 1024; // 50MB

export function MediaUpload({
  projectId,
  onUpload,
  maxFiles = 10,
  selectedMedia = [],
  onMediaSelect,
}: MediaUploadProps) {
  const { t } = useTranslation('media');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadMedia, isUploading, error } = useMediaStore();
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const validateFile = (file: File): string | null => {
    const isImage = ALLOWED_IMAGE_TYPES.includes(file.type);
    const isVideo = ALLOWED_VIDEO_TYPES.includes(file.type);
    const isAudio = ALLOWED_AUDIO_TYPES.includes(file.type);

    if (!isImage && !isVideo && !isAudio) {
      return t('allowedFormats');
    }

    const maxSize = isImage ? MAX_IMAGE_SIZE : isVideo ? MAX_VIDEO_SIZE : MAX_AUDIO_SIZE;
    if (file.size > maxSize) {
      return t('fileTooLarge', { size: maxSize / 1024 / 1024 });
    }

    return null;
  };

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setUploadError(null);

    for (let i = 0; i < Math.min(files.length, maxFiles - selectedMedia.length); i++) {
      const file = files[i];
      const validationError = validateFile(file);

      if (validationError) {
        setUploadError(validationError);
        continue;
      }

      try {
        const media = await uploadMedia(projectId, file);
        onUpload?.(media);
        if (onMediaSelect) {
          onMediaSelect([...selectedMedia, media]);
        }
      } catch {
        // Error handled in store
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleRemoveMedia = (mediaId: string) => {
    if (onMediaSelect) {
      onMediaSelect(selectedMedia.filter((m) => m.id !== mediaId));
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4">
      {/* Upload Zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          dragOver
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
        onClick={() => !isUploading && fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm,audio/mpeg,audio/mp3,audio/wav,audio/aac,audio/ogg,audio/webm,audio/x-m4a,audio/mp4"
          multiple
          className="hidden"
          onChange={(e) => handleFileSelect(e.target.files)}
          disabled={isUploading}
        />

        <div className="flex flex-col items-center">
          {isUploading ? (
            <>
              <svg className="animate-spin h-8 w-8 text-blue-600 mb-2" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-sm text-gray-600">{t('uploading')}</p>
            </>
          ) : (
            <>
              <svg className="w-10 h-10 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-sm text-gray-600 mb-1">
                {t('dragDropShort')} <span className="text-blue-600">{t('browse')}</span>
              </p>
              <p className="text-xs text-gray-400">
                {t('maxFileSizeInfo')}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Error Messages */}
      {(uploadError || error) && (
        <div className="bg-red-50 text-red-700 text-sm p-3 rounded-md">
          {uploadError || error}
        </div>
      )}

      {/* Selected Media Preview */}
      {selectedMedia.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {selectedMedia.map((media) => (
            <div key={media.id} className="relative group">
              {media.mime_type.startsWith('image/') ? (
                <img
                  src={media.url}
                  alt={media.original_name}
                  className="w-full h-24 object-cover rounded-lg"
                />
              ) : media.mime_type.startsWith('video/') ? (
                <div className="w-full h-24 bg-gray-900 rounded-lg flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              ) : media.mime_type.startsWith('audio/') ? (
                <div className="w-full h-24 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                </div>
              ) : (
                <div className="w-full h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
                  </svg>
                </div>
              )}
              {/* Remove Button */}
              <button
                onClick={() => handleRemoveMedia(media.id)}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              {/* File Info */}
              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg truncate">
                {formatFileSize(media.size)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* File Count */}
      {maxFiles > 1 && (
        <p className="text-xs text-gray-500 text-center">
          {t('filesSelected', { current: selectedMedia.length, max: maxFiles })}
        </p>
      )}
    </div>
  );
}
